import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { ProductCardData, ProductDetailData, ShellData } from '@/types'

import { Boxes, Layers, Package, ScanLine, ShoppingBag } from 'lucide-react'
import { toLocale, useI18n } from '@/lib/i18n'
import { unitLabel } from '@/constants/units'
import { Seo } from '@/components/generals/seo'
import { StoreLayout } from '@/components/store/store-layout'
import { CmsText } from '@/components/store/cms-text'
import { Breadcrumbs } from '@/components/store/page-header'
import { ProductGallery } from '@/components/store/product-gallery'
import { ProductPurchase } from '@/components/store/product-purchase'
import { ProductGrid } from '@/components/store/product-card'
import { SectionHeading } from '@/components/store/section-heading'
import { Num } from '@/components/store/price'
import { getProductDetail, getRelatedProducts, loadOffers, loadShell } from '@/lib/server/store'

interface ProductPageProps {
    shell: ShellData
    product: ProductDetailData
    related: Array<ProductCardData>
    serverNow: number
}

const ProductPage: FC<ProductPageProps> = ({ shell, product, related, serverNow }): ReactNode => {
    const { tx, locale } = useI18n()
    const { settings } = shell
    const path = `/products/${product.slug}`
    const pageUrl = shell.origin ? `${shell.origin}${locale === 'en' ? '/en' : ''}${path}` : ''
    const description = product.shortDescription?.v || product.description?.v.slice(0, 160) || product.name.v

    const specs: Array<{ icon: typeof Package, label: string, value: ReactNode }> = [
        { icon: Package, label: tx('وحدة البيع', 'Sold per'), value: unitLabel(product.unit, locale) },
        { icon: ShoppingBag, label: tx('أقل كمية للطلب', 'Minimum order'), value: <Num value={product.minOrderQty} /> }
    ]

    if (product.pricing.wholesale !== null) {
        specs.push({
            icon: Layers,
            label: tx('سعر الجملة يبدأ من', 'Wholesale price from'),
            value: (
                <>
                    <Num value={product.wholesaleMinQty} />
                    {' '}
                    {unitLabel(product.unit, locale, product.wholesaleMinQty)}
                </>
            )
        })
    }

    if (product.sku) specs.push({ icon: ScanLine, label: tx('كود المنتج', 'Product code'), value: <span dir='ltr'>{product.sku}</span> })
    if (product.variants.length > 0) specs.push({ icon: Boxes, label: tx('الاختيارات المتاحة', 'Available options'), value: <Num value={product.variants.length} /> })

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name.v,
        description,
        sku: product.sku || undefined,
        image: product.gallery.map(image => image.src.startsWith('http') ? image.src : `${shell.origin}${image.src}`),
        category: product.category?.name.v,
        brand: { '@type': 'Brand', name: settings.storeName.v },
        offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'EGP',
            lowPrice: product.pricing.wholesaleFinal ?? product.pricing.retailFinal,
            highPrice: product.pricing.retail,
            availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            url: pageUrl || undefined
        }
    }

    return (
        <StoreLayout shell={shell}>
            <Seo
                title={product.name.v}
                description={description}
                siteName={settings.storeName.v}
                origin={shell.origin}
                path={path}
                ogImage={product.gallery[0]?.src}
                jsonLd={jsonLd}
            />

            <div className='bg-gradient-to-b from-navy-50/70 to-white'>
                <div className='container-page pt-6 pb-4 sm:pt-8'>
                    <Breadcrumbs
                        items={[
                            { label: tx('المنتجات', 'Products'), href: '/products' },
                            ...(product.category ? [{ label: <CmsText table='categories' id={product.category.id} field={product.category.name} />, href: `/categories/${product.category.slug}` }] : []),
                            { label: <CmsText table='products' id={product.id} field={product.name} /> }
                        ]}
                    />
                </div>

                <section id='product' className='container-page grid gap-8 pb-16 lg:grid-cols-12 lg:gap-12'>
                    <div className='lg:col-span-6 xl:col-span-7'>
                        <div className='lg:sticky lg:top-24'>
                            <ProductGallery images={product.gallery} fallbackAlt={product.imageAlt} categorySlug={product.category?.slug ?? null} />
                        </div>
                    </div>

                    <div className='flex flex-col gap-6 lg:col-span-6 xl:col-span-5'>
                        <div className='flex flex-col gap-3'>
                            {product.category && (
                                <CmsText table='categories' id={product.category.id} field={product.category.name} className='text-sm font-semibold text-cyan-700' />
                            )}
                            <CmsText table='products' id={product.id} field={product.name} as='h1' className='text-balance text-3xl font-bold leading-tight tracking-tight text-navy-950 sm:text-4xl' />
                            {product.shortDescription && (
                                <CmsText table='products' id={product.id} field={product.shortDescription} as='p' className='text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg' />
                            )}
                        </div>

                        <ProductPurchase product={product} settings={settings} serverNow={serverNow} pageUrl={pageUrl} />
                    </div>
                </section>
            </div>

            <section id='details' className='border-t border-navy-100 bg-white'>
                <div className='container-page grid gap-10 py-14 lg:grid-cols-12'>
                    <div className='flex flex-col gap-4 lg:col-span-7'>
                        <h2 className='text-2xl font-bold tracking-tight text-navy-950'>{tx('تفاصيل المنتج', 'Product details')}</h2>
                        {product.description
                            ? <CmsText table='products' id={product.id} field={product.description} as='p' className='whitespace-pre-line text-pretty text-base leading-loose text-navy-700' />
                            : product.shortDescription && <CmsText table='products' id={product.id} field={product.shortDescription} as='p' className='text-base leading-loose text-navy-700' />}
                    </div>

                    <div className='lg:col-span-5'>
                        <dl className='divide-y divide-navy-100 rounded-3xl bg-surface p-2 ring-1 ring-navy-100'>
                            {specs.map(spec => (
                                <div key={spec.label} className='flex items-center justify-between gap-4 px-4 py-3.5'>
                                    <dt className='flex items-center gap-2.5 text-sm text-navy-600'>
                                        <spec.icon className='size-4 text-navy-400' aria-hidden='true' />
                                        {spec.label}
                                    </dt>
                                    <dd className='text-sm font-semibold text-navy-950'>{spec.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </section>

            {related.length > 0 && (
                <section id='related' className='bg-surface py-14 sm:py-16' aria-labelledby='related-title'>
                    <div className='container-page'>
                        <SectionHeading id='related-title' title={tx('منتجات قد تعجبك', 'You may also like')} action={product.category ? { href: `/categories/${product.category.slug}`, label: tx('المزيد من نفس القسم', 'More from this category') } : undefined} />
                        <ProductGrid products={related} className='mt-8' />
                    </div>
                </section>
            )}
        </StoreLayout>
    )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async ({ locale: rawLocale, req, params }) => {
    const locale = toLocale(rawLocale)
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const offersPromise = loadOffers()
    const [shell, offers, product] = await Promise.all([loadShell(locale, req), offersPromise, offersPromise.then(loaded => getProductDetail(slug, locale, loaded))])

    if (!product) return { notFound: true }

    const related = await getRelatedProducts(product, locale, offers)

    return { props: { shell, product, related, serverNow: Date.now() } }
}

export default ProductPage
